<template>
  <div
    :class="layoutClass"
    class="result-item">
    <div class="result-item__content">
      <div
        v-if="content.searchGroupName"
        class="result-item__group">
        <a-icon :icon="icon" />
        {{ content.searchGroupName }}
      </div>

      <nuxt-link
        :to="content.url"
        class="result-item__link">
        <h3 class="result-item__name">
          {{ displayName }}
        </h3>
      </nuxt-link>

      <p
        v-if="trimmedDescription"
        class="result-item__description">
        {{ trimmedDescription }}
      </p>

      <div class="result-item__action">
        Read more
        <a-icon
          icon="fa:regular:chevron-right"
          size="xs" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import type { SearchResultDto } from "~/composables/useSupportSearchResource";

export default defineComponent( {
  props: {
    content: {
      type: Object as PropType<SearchResultDto>,
      required: true,
      default: () => ( {} ),
    },
    layout: {
      type: String,
      required: false,
      default: "",
    },
  },
  computed: {
    layoutClass () {
      return `layout-${this.layout}`;
    },
    displayName () {
      let result = "";

      const isDefined = this.content.searchTitle !== null
        && this.content.searchTitle;

      if ( isDefined && ( this.content.searchTitle?.length ?? 0 ) > 0 ) {
        result = this.content.searchTitle || "";
      } else {
        result = this.content.name;
      }

      return result.replace( /&nbsp;|\xA0/g, " " );
    },
    icon () {
      let result = "fa:regular:book";

      switch ( this.content.contentType ) {
        case "assessmentArticle":
        case "assessmentItem": {
          result = "fa:regular:box";
          break;
        }
        case "platformArticle":
        case "platformItem": {
          result = "fa:regular:layer-group";
        }
      }

      return result;
    },
    trimmedDescription () {
      let result = this.content.searchDescription || "";

      if ( result != null && result.length > 75 ) {
        result = result.substring( 0, 72 );

        const indexOfLastSpace = result.lastIndexOf( " " );
        result = result.substring( 0, indexOfLastSpace );

        result += "...";
      }

      return result.replace( /&nbsp;|\xA0/g, " " );
    },
  },
} );
</script>

<style lang="scss" scoped>
.result-item {
  display: flex;
  flex-direction: column;
  width: 100%;
  flex-grow: 1;
  border-bottom: 1px solid #aaa;
  text-decoration: none !important;
  position: relative;

  &__image-wrapper {
    display: block;
    border-radius: 5px 5px 0 0;
    cursor: pointer;
  }

  &__group {
    font-weight: 500;
    color: #333;
    text-decoration: none;
    text-transform: uppercase;
    margin-bottom: 5px;
    display: flex;
    align-items: center;

    svg {
      display: inline-block;
      margin-right: 5px;
    }
  }

  &__content {
    flex-grow: 1;
    padding: 20px 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
  }

  &__link {
    text-decoration: none;

    &::before {
      content: "";
      position: absolute;
      inset: 0;
      z-index: 2;
    }
  }

  &__name {
    color: #434343;
    font-size: 1.25rem;
    margin-bottom: 15px;
    font-weight: 500;
  }

  &__description {
    margin-bottom: 15px;
    color: var( --color-secondary-500 );
  }

  &__action {
    position: relative;
    text-decoration: none !important;
    font-weight: 500;
    padding-bottom: 5px;
    text-transform: uppercase;
    display: inline-flex;
    align-items: center;
    color: var( --color-primary-500 );

    svg {
      display: inline-block;
      margin-left: 3px;
    }
  }

  &:hover &__action {
    color: var( --color-primary-700 );
  }

  @media ( width <= 767px ) {
    max-width: 440px;
    margin-left: auto;
    margin-right: auto;
  }
}

.search-results__item:first-child .result-item {
  border-top: 1px solid #aaa;
}
</style>
